///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GtthReportsService } from '../../../../services/project/reports.service';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { finalize, Subscription } from 'rxjs';
import { GtthReportsTabs, REPORT_TABS } from '../reports-tabs.enum';
import { ProjectProductsService } from '../../../logical-topology/engine/project-products-service';
import { ExtendedLinkData } from './extended-link-data.model';
import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { _cloneDeep, scrollToEl } from '../../../../shared/utils';
import { GtthExportService } from '../../../../services/project/export.service';
import { ActivatedRoute } from '@angular/router';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { GtthProjectInfoViewService } from '../../../../layouts/navbar/project-info-view.service';
import { MatDialogRef } from '@angular/material/dialog';
import { LoaderIds, PreloaderService } from '../../../../services/preloader.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-report-p2p-info',
    templateUrl: './report-p2p-info.component.html',
    styleUrls: ['../../project-windows.scss', './report-p2p-info.component.scss'],
    standalone: false
})
export class GtthReportP2PInfoComponent extends AbstractInteractiveWindow implements OnInit, OnDestroy {
    public reportData: ExtendedLinkData[];
    public reportDataToShow: ExtendedLinkData[];
    public currentTab = GtthReportsTabs.ALL;
    public reportsTabs = GtthReportsTabs;
    public readonly tabsToShow = REPORT_TABS;
    public layers: Record<string, string>;
    public readonly loaderKey = LoaderIds.REPORT_P2P_INFO_ID;
    @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;
    private layersChangedSubscriber: Subscription;

    constructor(
        public readonly preloaderService: PreloaderService,
        public readonly historyService: GtthHistoryService,
        private readonly elementRef: ElementRef,
        private readonly dialogRef: MatDialogRef<GtthReportP2PInfoComponent>,
        private readonly reportsService: GtthReportsService,
        private readonly projectProductsService: ProjectProductsService,
        private readonly exportService: GtthExportService,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {
        super(elementRef);

        this.layersChangedSubscriber = projectInfoService.layerChangedSubscription().subscribe((value) => {
            if (this.reportData) {
                this.filterReport(value);
            }
            this.getProjectLayers();
        });
    }

    ngOnInit() {
        this.getProjectLayers();

        this.preloaderService.show(this.loaderKey);
        this.reportsService
            .generateP2PInfoReport()
            .pipe(finalize(() => this.preloaderService.hide(this.loaderKey)))
            .subscribe((res: ExtendedLinkData[]) => {
                this.reportData = res;
                this.getLinksFrozenStatus(this.reportData);
                this.filterReport(this.projectInfoService.activeLayer);
                for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
                    const fromNode = link.getFromStation().getName();
                    const toNode = link.getToStation().getName();
                    this.linkSelected(fromNode, toNode, true);
                }
            });
    }

    ngOnDestroy() {
        if (this.layersChangedSubscriber) {
            this.layersChangedSubscriber.unsubscribe();
            this.layersChangedSubscriber = null;
        }
    }

    getProjectLayers() {
        if (this.projectInfoService.layersOptions?.length) {
            this.layers = this.projectInfoService.getLayersAsKeyValueObj();
        }
    }

    linkFrozen(fromName: string, toName: string, isForced: boolean) {
        this.changeLinkFrozenStatus(fromName, toName, isForced);
    }

    linkInstalled(fromName: string, toName: string, isInstalled: boolean) {
        this.changeLinkFrozenStatus(fromName, toName, undefined, isInstalled);
    }

    trackLinkData(_index, item: ExtendedLinkData) {
        return item.desc;
    }

    closeModal() {
        this.reportData = undefined;
        this.dialogRef.close('cancel');
    }

    exportToExcel() {
        this.exportService.exportP2pReport(this.projectInfoService.activeLayer);
    }

    linkSelected(fromName: string, toName: string, selected: boolean) {
        const linkIndex = this.reportDataToShow.findIndex((link) => {
            return link.node0Id === fromName && link.node1Id === toName;
        });
        if (linkIndex !== -1) {
            this.reportDataToShow[linkIndex].selected = selected;
        }
    }

    changeTab(tabIndex: string) {
        this.currentTab = GtthReportsTabs[tabIndex];
        this.filterReport(this.projectInfoService.activeLayer);
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return true;
    }

    isRestorable(): boolean {
        return true;
    }

    private filterReport(layerIndex = -1) {
        switch (this.currentTab) {
            case GtthReportsTabs.ALL:
                this.reportAll(layerIndex);
                break;

            case GtthReportsTabs.NEW:
                this.reportNew(layerIndex);
                break;

            case GtthReportsTabs.INSTALLED:
                this.reportInstalled(layerIndex);
                break;
        }
    }

    private getLinksFrozenStatus(linksData: ExtendedLinkData[]) {
        linksData.forEach((linkData) => {
            const nodeNames = linkData.desc.split(' - ');
            const link = this.historyService.logicalTopology.getLinkByNodeNames(nodeNames[0], nodeNames[1]);
            if (!link) {
                return;
            }
            linkData.isUserForced = link.isUserForced();
            linkData.isInstalled = link.isInstalled();
            linkData.usedProductName = this.projectProductsService.getBaseRadioNameById(linkData.usedProd);
        });
    }

    private changeLinkFrozenStatus(fromName: string, toName: string, isForced: boolean, isInstalled?: boolean) {
        const link = this.reportDataToShow.find((linkData: ExtendedLinkData) => {
            return (
                (linkData.node0Id === fromName || linkData.node1Id === fromName) &&
                (linkData.node0Id === toName || linkData.node1Id === toName)
            );
        });

        if (!link) {
            return;
        }
        link.isUserForced = isDefined(isForced) ? isForced : link.isUserForced;
        link.isInstalled = isDefined(isInstalled) ? isInstalled : link.isInstalled;
        if (this.scrollContainer) {
            scrollToEl(this.scrollContainer.nativeElement, `.p2p-link-row--${link.node0Id}-${link.node1Id}`);
        }
    }

    private reportAll(layerIndex: number): void {
        if (layerIndex === -1) {
            this.reportDataToShow = _cloneDeep(this.reportData);
        } else {
            this.reportDataToShow = [];
            const linkData = this.reportData.filter((link) => {
                return link.layerIndex === layerIndex;
            });
            if (linkData && linkData.length > 0) {
                this.reportDataToShow = [...linkData];
            }
        }
    }

    private reportNew(layerIndex: number): void {
        this.reportDataToShow = [];
        const newLinkData = this.reportData.filter((link) => {
            return !link.isUserForced && (layerIndex === -1 || link.layerIndex === layerIndex);
        });
        if (newLinkData && newLinkData.length > 0) {
            this.reportDataToShow = [...newLinkData];
        }
    }

    private reportInstalled(layerIndex: number): void {
        this.reportDataToShow = [];
        const installedLinkData = this.reportData.filter((link) => {
            return link.isInstalled && (layerIndex === -1 || link.layerIndex === layerIndex);
        });
        if (installedLinkData && installedLinkData.length > 0) {
            this.reportDataToShow = [...installedLinkData];
        }
    }
}

@Component({
    selector: 'winde-report-p2p-info-popup',
    template: '',
    standalone: false
})
export class GtthReportP2PInfoPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportP2PInfoComponent,
            ProjectInteractiveWindowsPopupService.REP_P2P_INFO,
            routerPath,
            bboxParams
        );
    }
}
